import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { priceFormatter } from '../../utils/formatters';
import { addSeat, removeSeat, setHoldToken, clearSeats, PromotionType } from '../../redux/slices/cartSlice';
import { useMediaQuery, useTheme, Box, Typography } from '@mui/material';
import { SeatsioEventManager } from '@seatsio/seatsio-react';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import CartIcon from '@mui/icons-material/ShoppingCart';
import MobileDrawer from './MobileDrawer';
import DesktopDrawer from './DesktopDrawer';
import NPlusOneFreeDialog from './NPlusOneFreeDialog';
import ChartLegend from '../ChartLegend';
import api from './../../api.js';

const drawerWidth = 340;

const EVENT_INITIAL_STATE = {
  title: '-',
  location: '-',
  city: '-',
  date: '',
  has_event_date: 1,
  scan_code: '',
  is_one_plus_one_free: false,
  is_three_plus_one_free: false,
  premiere: false,
  national_premiere: false,
  second_representation: false,
  platform_comission: 0,
  seat_price: 0,
  comission_cash_per_seat: false,
  notification_sms_price: 0,
  notifiction_email_price: 0,
  status: 'SELLING',
  can_delete: false,
  index_for_search: true,
};

function OrdersDialog({ open, onClose, eventId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.auth.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openNPlusOneDialog, setOpenNPlusOneDialog] = useState(false);
  const [event, setEvent] = useState(JSON.parse(JSON.stringify(EVENT_INITIAL_STATE)));
  const [categoryPricings, setCategoryPricings] = useState([]);
  const chartRef = useRef(null);
  const seats = useSelector(({ cart: { seats } }) => seats);
  const totalPrice = useSelector(({ cart: { totalPrice } }) => totalPrice);

  // eslint-disable-next-line no-unused-vars
  const [isEventPending, setEventPending] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isPricingsPending, setPricingsPending] = useState(true);

  const { title, date, has_event_date, city, seatsio_event_id, status, is_one_plus_one_free, is_three_plus_one_free } =
    event;

  const fullEventTitle = useMemo(
    () =>
      has_event_date === 0 ? `${title} ${city}` : `${title} ${city} - ${dayjs(date).format('dddd DD MMMM YYYY HH:mm')}`,
    [title, city, date, has_event_date]
  );

  const currentEventSeats = useMemo(
    () => seats.filter((s) => s.seatsioEventId === seatsio_event_id),
    [seats, seatsio_event_id]
  );

  const promotionType = useMemo(() => {
    if (is_one_plus_one_free) {
      return PromotionType.ONE_PLUS_ONE;
    } else if (is_three_plus_one_free) {
      return PromotionType.THREE_PLUS_ONE;
    } else {
      return PromotionType.NONE;
    }
  }, [is_one_plus_one_free, is_three_plus_one_free]);

  useEffect(() => {
    if (promotionType !== PromotionType.NONE && status === 'SELLING') {
      setOpenNPlusOneDialog(true);
    }
    return () => {
      setOpenNPlusOneDialog(false);
    };
  }, [promotionType, status]);

  useEffect(() => {
    if (open && eventId) {
      const eventsPromise = api.get(`/events/${eventId}`).then((res) => {
        if (res.data && res.data.data && res.data.data.length > 0) {
          setEvent(res.data.data[0]);
        }
        setEventPending(false);
      });

      const pricingsPromise = api.get(`/events/${eventId}/categoryPricings`).then((res) => {
        if (res.data && res.data.data) {
          setCategoryPricings(
            res.data.data.map(({ price, discount, ...rest }) => ({
              price: Number(price),
              discount: Number(discount),
              ...rest,
            }))
          );
        }
        setPricingsPending(false);
      });

      Promise.all([eventsPromise, pricingsPromise]).catch((err) => {
        console.log('Error fetching data ', err);
        toast.error(t('An error occured please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
    }
  }, [user.role, eventId, open, setPricingsPending, setEventPending, t]);

  useEffect(() => {
    if (!open) {
      setEvent(JSON.parse(JSON.stringify(EVENT_INITIAL_STATE)));
      setEventPending(true);
      setPricingsPending(true);
      setCategoryPricings([]);
    }
  }, [dispatch, open]);

  const prices = useMemo(() => {
    const ticketsByCatId = categoryPricings.reduce(
      (acc, { categories_id, ...rest }) => ({ ...acc, [categories_id]: [...(acc[categories_id] || []), rest] }),
      {}
    );

    return Object.keys(ticketsByCatId).map((categoryId) => {
      const ticketTypes = ticketsByCatId[categoryId];

      if (ticketTypes.length === 1) {
        const { price, discount } = ticketTypes[0];
        let actualPrice = price;
        if (discount) {
          actualPrice -= price * discount;
        }
        return { category: categoryId, price: actualPrice, ticketTypes: [] };
      } else {
        return {
          category: categoryId,
          ticketTypes: ticketTypes.map(({ type_id, type_label, price, discount }) => {
            let actualPrice = price;
            if (discount) {
              actualPrice -= price * discount;
            }
            return { ticketType: String(type_id), price: actualPrice, label: type_label };
          }),
        };
      }
    });
  }, [categoryPricings]);

  const onChartRendered = async (chart) => {
    chartRef.current = chart;

    if (prices.length && currentEventSeats.length) {
      const selectedObjects = currentEventSeats.map((seat) => {
        const multiType = prices.some((t) => Number(t.category) === seat.categoryId && t.ticketTypes.length > 0);
        return { label: seat.id, ...(multiType ? { ticketType: String(seat.typeId) } : {}), amount: 1 };
      });

      for (const seat of currentEventSeats) {
        const { id, eventId, promotionType, singleType, typeId } = seat;
        const seatObj = {
          id,
          eventId,
          promotionType,
          typeId: singleType ? null : String(typeId),
        };
        dispatch(removeSeat(seatObj));
      }

      try {
        chart.doSelectObjects(selectedObjects);
      } catch (err) {
        console.log('Error selecting seats ', err);
        toast.error(t('One or more of the selected seats are not available anymore!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }
  };

  const onObjectSelected = (selectedObj, selectedTicketType) => {
    const {
      seatId,
      labels,
      pricing: { price },
      category: { key, label, color },
    } = selectedObj;

    const { section, parent: row, own: seat } = labels;

    const priceInfos = categoryPricings.filter((pricing) => pricing.categories_id === key);

    const priceInfo = selectedTicketType
      ? priceInfos.filter((pricing) => String(pricing.type_id) === selectedTicketType.ticketType)[0]
      : priceInfos[0];
    const actualPrice = selectedTicketType ? selectedTicketType.price : price;

    const seatObj = {
      eventTitle: fullEventTitle,
      eventId,
      seatsioEventId: seatsio_event_id,
      promotionType,
      id: seatId,
      singleType: !selectedTicketType,
      typeId: priceInfo.type_id,
      typeLabel: priceInfo.type_label,
      isDefaultType: priceInfo.is_default_type,
      section,
      row,
      seat,
      basePrice: priceInfo.price,
      discount: priceInfo.discount,
      price: actualPrice,
      categoryId: key,
      categoryLabel: label,
      categoryColor: color,
    };

    dispatch(addSeat(seatObj));
  };

  const onObjectDeselected = (deselectedObj, deselectedTicketType) => {
    const { seatId } = deselectedObj;

    const seatObj = {
      id: seatId,
      eventId,
      promotionType,
      ...(deselectedTicketType ? { typeId: deselectedTicketType.ticketType } : {}),
    };

    dispatch(removeSeat(seatObj));
  };

  const onRemoveAll = () => {
    if (!chartRef || !chartRef.current) return;
    chartRef.current.clearSelection();
  };

  const onRemove = async (seatId, ticketType) => {
    if (!chartRef || !chartRef.current) return;
    const selectedObjects = await chartRef.current.listSelectedObjects();
    const isSelected = !!selectedObjects.find((obj) => obj.id === seatId);
    if (isSelected) {
      chartRef.current.deselectObjects([
        { label: seatId, ...(ticketType ? { ticketType: String(ticketType) } : {}), amount: 1 },
      ]);
    } else {
      onObjectDeselected({ seatId }, ticketType);
    }
  };

  const onHoldTokenExpired = () => {
    dispatch(clearSeats());
  };

  const onSessionInitialized = ({ token }) => {
    dispatch(setHoldToken(token));
  };

  const drawerProps = {
    drawerWidth,
    seats: currentEventSeats,
    allSeats: seats,
    totalPrice,
    onRemove,
    onRemoveAll,
    onCancel: onClose,
  };

  return (
    <Dialog fullScreen={true} open={open} onClose={onClose}>
      <Box sx={{ width: `calc(100% - ${!isMobile ? drawerWidth : 0}px)` }}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <CartIcon sx={{ mr: 1, flexShrink: 0 }} />
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexGrow: 1,
            }}
          >
            {fullEventTitle}
          </Box>
        </DialogTitle>

        <Divider />
        <ChartLegend categories={categoryPricings} inline={isMobile} />
      </Box>
      <DialogContent sx={{ padding: 0 }}>
        <Box style={{ height: '100%', width: `calc(100% - ${!isMobile ? drawerWidth : 0}px)` }}>
          {(status === 'ARCHIVED' || status === 'DELETED') && (
            <Box sx={{ height: '100%', width: '100%' }} display="flex" alignItems="center" justifyContent="center">
              <Typography variant="h4">{t('Event Unavailable')}</Typography>
            </Box>
          )}
          {seatsio_event_id && status === 'SELLING' && (
            <>
              <SeatsioEventManager
                mode="createOrder"
                // selectionValidators={[{ type: 'noOrphanSeats' }]}
                objectWithoutPricingSelectable={false}
                viewSettingsDefaults={{ showSeatLabels: true, showRowLabels: true }}
                legend={{ hideNonSelectableCategories: true }}
                secretKey={process.env.REACT_APP_SEATSIO_PRIVATE_WORKSPACE_KEY}
                event={seatsio_event_id}
                session="none"
                onChartRendered={onChartRendered}
                onSessionInitialized={onSessionInitialized}
                onHoldTokenExpired={onHoldTokenExpired}
                pricing={prices}
                priceFormatter={priceFormatter}
                showFullScreenButton={false}
                onObjectSelected={onObjectSelected}
                onObjectDeselected={onObjectDeselected}
                region={process.env.REACT_APP_SEATSIO_REGION}
              />
            </>
          )}
          {seatsio_event_id && status === 'NOT_SELLING' && (
            <>
              <SeatsioEventManager
                mode="static"
                viewSettingsDefaults={{ showSeatLabels: true, showRowLabels: true }}
                secretKey={process.env.REACT_APP_SEATSIO_PRIVATE_WORKSPACE_KEY}
                event={seatsio_event_id}
                onChartRendered={onChartRendered}
                showFullScreenButton={false}
                onObjectSelected={onObjectSelected}
                onObjectDeselected={onObjectDeselected}
                region={process.env.REACT_APP_SEATSIO_REGION}
              />
            </>
          )}
        </Box>
        {!isMobile ? <DesktopDrawer {...drawerProps} /> : <MobileDrawer {...drawerProps} />}
      </DialogContent>
      <NPlusOneFreeDialog
        promotionType={promotionType}
        open={openNPlusOneDialog}
        onConfirm={() => setOpenNPlusOneDialog(false)}
      />
    </Dialog>
  );
}

export default OrdersDialog;
